import ViewFormats from '@anm/components/ViewFormats';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { VideoSizes, VideoStatusCode } from '@anm/hosting-player';
import { useRef, FC } from 'react';
import { useSelector } from 'react-redux';

import { APPEARANCE_TAB, EMAIL_TAB, EMBED_TAB, LANDING_TAB, PREVIEW_TAB } from '../../../../pages/video';
import vtConfig from '../../../../config';
import { hostedVideoSelectors } from '../../../../store/modules/hostedVideo';
import { landingsSelectors } from '../../../../store/modules/landings';
import { ChangeHandlers } from '../../types';

import BlockedVideo from './BlockedVideo';
import EmailThumb from './EmailThumb';
import EmbedPreview from './EmbedPreview';
import PlayerBlock from './PlayerBlock';
import RightPanelWrapper, {
  LandingFrameWrapper,
  PreviewTitle,
  SaveButton,
  SaveButtonWrapper,
  StickyWrapper
} from './Wrapper';
import {
  useIframeCommunication,
  useIframeSizes,
  useLandingPreviewHover,
  useTrackOpenPreview,
  useTurnOffFooter
} from './hooks';

export type RightPanelProps = {
  sizes: VideoSizes;
  hostId: string;
  projectId: string;
  activeButton: number;
  setCurrentTime: (time: number) => void;
  errorCode?: VideoStatusCode;
  errorMessage?: string;
} & ChangeHandlers;

const RightPanel: FC<RightPanelProps> = ({ sizes, wasChanged, getHandlers, activeButton, ...playerBlockProps }) => {
  const { hostId, errorCode } = playerBlockProps;

  const container = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const isEmailScreen = activeButton === EMAIL_TAB;
  const isEmbedScreen = activeButton === EMBED_TAB;
  const isLandingScreen = activeButton === LANDING_TAB;
  const isPreviewScreen = activeButton === PREVIEW_TAB;
  const isAppearanceTab = activeButton === APPEARANCE_TAB;

  const [iframeWidth, iframeHeight, viewType, handleTypeChange] = useIframeSizes(container, isLandingScreen);

  const [hoverRef, isHovered] = useLandingPreviewHover();

  const canRenderBlockedVideo =
    playerBlockProps.errorCode === VideoStatusCode.blocked && (isPreviewScreen || isAppearanceTab);

  const canRenderPlayer = !isEmailScreen && !isLandingScreen && !isEmbedScreen && !canRenderBlockedVideo;
  const canRenderIframe = isLandingScreen && iframeWidth;
  const canShowViewFormats = isLandingScreen && !isMobileOrIPad();

  const videoData = useSelector(hostedVideoSelectors.selectVideoData);
  const currentLanding = useSelector(landingsSelectors.selectLanding);

  useTrackOpenPreview(isPreviewScreen);

  useIframeCommunication({
    viewType,
    iframeRef,
    landing: currentLanding
  });

  useTurnOffFooter(isLandingScreen, currentLanding);

  const iframeSrc = `${vtConfig().urls.videoLanding}${hostId}?target=customization&force=true`;

  const { save, isPending } = getHandlers();

  const isLandingTabOnMobiles = isMobileOrIPad() && isLandingScreen;
  const isMobilePreview = viewType === 'mobile';
  const isFullSizePreview = viewType === 'fullscreen';

  return (
    <RightPanelWrapper ref={container} {...{ isLandingScreen, isFullSizePreview }}>
      <StickyWrapper ref={hoverRef} {...{ isMobilePreview }}>
        {canShowViewFormats && <ViewFormats {...{ isHovered }} onChange={handleTypeChange} selectedItem={viewType} />}
        {isLandingTabOnMobiles && <PreviewTitle title="Preview" level={4} />}
        {canRenderIframe && <LandingFrameWrapper ref={iframeRef} src={iframeSrc} {...{ iframeHeight, iframeWidth }} />}
        {canRenderPlayer && (
          <PlayerBlock
            {...{
              sizes,
              container,
              videoData,
              activeButton,
              ...playerBlockProps
            }}
          />
        )}
        {canRenderBlockedVideo && <BlockedVideo {...{ sizes, container }} />}
        {isEmailScreen && <EmailThumb {...{ hostId, sizes }} />}
        {isEmbedScreen && <EmbedPreview {...{ hostId, errorCode }} videoName={videoData?.meta.name} />}
        {isLandingTabOnMobiles && (
          <SaveButtonWrapper>
            <SaveButton
              disabled={!wasChanged}
              onClick={save}
              variant="light-blue"
              isPending={isPending}
              size="long_static_width"
            >
              {wasChanged ? 'Save' : 'Saved'}
            </SaveButton>
          </SaveButtonWrapper>
        )}
      </StickyWrapper>
    </RightPanelWrapper>
  );
};

export default RightPanel;
